import axios from "axios";
import { ApiList, ApiResponse } from "types/api.types";
import {
  AddPaymentRequest,
  Payment,
  PaymentSummaryData,
} from "features/Payments/types/payment.types";
import axiosClient from "utils/axios";

function sanitizePaymentReceiptFilename(code: string): string {
  const s = code.replace(/[^a-zA-Z0-9._-]+/g, "_");
  return (s || "payment").slice(0, 120);
}

function filenameFromContentDisposition(
  header: string | undefined,
  fallback: string,
): string {
  if (!header) return fallback;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].replace(/["']/g, "").trim());
    } catch {
      /* ignore */
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted?.[1]) return quoted[1];
  const unquoted = /filename=([^;\s]+)/i.exec(header);
  if (unquoted?.[1]) return unquoted[1].replace(/["']/g, "");
  return fallback;
}

async function messageFromErrorBlob(blob: Blob): Promise<string> {
  const text = await blob.text();
  try {
    const j = JSON.parse(text) as { message?: string };
    return j.message?.trim() || text || "Request failed";
  } catch {
    return text?.trim() || "Request failed";
  }
}

/** GET /payments/:id/receipt — PDF; backend requires POSTED status and payment.read */
export async function downloadPaymentReceipt(
  id: number,
  codeForFallback?: string | null,
): Promise<void> {
  if (!Number.isInteger(id) || id <= 0) {
    throw new Error("Invalid payment id");
  }
  try {
    const response = await axiosClient.get(`/payments/${id}/receipt`, {
      responseType: "blob",
    });

    const base = sanitizePaymentReceiptFilename(
      codeForFallback?.trim() || `payment-${id}`,
    );
    const fallback = `${base}-receipt.pdf`;
    const cd = response.headers["content-disposition"] as string | undefined;
    const filename = filenameFromContentDisposition(cd, fallback);

    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.data instanceof Blob) {
      const msg = await messageFromErrorBlob(err.response.data);
      throw new Error(msg);
    }
    throw err;
  }
}

export type GetPaymentSummaryParams =
  | { year: number; month: number }
  | { from: string; to: string };

export const getPaymentSummary = async (
  params: GetPaymentSummaryParams,
) => {
  const response = await axiosClient.get<ApiResponse<PaymentSummaryData>>(
    "/payments/summary",
    { params },
  );
  return response.data;
};

export const getPayments = async ({
  page = 1,
  limit = 10,
  search,
  status,
  sourceType,
  sourceId,
  payeeType,
  payeeId,
}: {
  page?: number;
  limit?: number;
  search?: string;
  /** Comma-separated PaymentStatus values. */
  status?: string;
  sourceType?: string;
  sourceId?: number;
  /** Currently only EMPLOYEE. */
  payeeType?: string;
  payeeId?: number;
} = {}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiList<Payment[]>>>(
      "/payments",
      {
        params: {
          page,
          limit,
          search,
          status,
          sourceType,
          sourceId,
          payeeType,
          payeeId,
        },
      },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Payment list : ", err);
    throw err;
  }
};

export const getPayment = async (id: number) => {
  try {
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error("Invalid ID");
    }
    const response = await axiosClient.get<ApiResponse<Payment>>(
      `/payments/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Payment details : ", err);
    throw err;
  }
};

export const createPayment = async (payload: AddPaymentRequest) => {
  try {
    const response = await axiosClient.post<ApiResponse<AddPaymentRequest>>(
      "/payments",
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error creating payment : ", err);
    throw err;
  }
};

/** POST /payments/:id/cancel — requires payment.cancel; typically POSTED only */
export const cancelPayment = async (id: number) => {
  try {
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error("Invalid ID");
    }
    const response = await axiosClient.post<ApiResponse<Payment>>(
      `/payments/${id}/cancel`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error cancelling payment : ", err);
    throw err;
  }
};
